import { PageWrapper, OvCard } from '@components'
import { DYNAMIC_LABELS, INVOICE_DEFAULT_VALUES, LABELS, TOOLTIPS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Check, Delete, Download, Edit, Save, SwapHoriz, Email, ContentCopy, Visibility } from '@mui/icons-material'
import { Box, Button, Stack, Grid, Typography, Tooltip, Skeleton } from '@mui/material'
import { ISingleDocument, IPaymentFormData, PaymentStatus } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { documentValidationSchema } from 'src/partials/forms/invoice/validation/document-line.form.validation'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { PaymentForm, PaymentList, PaymentSummary } from 'src/components/features/payment'
import { StatusBadge, StatusChangeMenu } from 'src/components/features/status-badge'
import { PAYMENT_LABELS } from 'src/constants/payment.constants'
import { SendEmailForm } from 'src/partials/forms/email'
import { RelatedDocuments } from 'src/components/features/document-relationships'
import {
  DownloadFormatModal,
  DownloadFormat,
} from 'src/components/features/download-format-modal/download-format-modal'
import { DocumentPreviewModal } from 'src/components/features/document-preview'

import { UpdateInvoiceForm } from 'src/partials/index.partials'

export const InvoiceDetail = observer(() => {
  const { ui, invoice, shared, client, payment, company, billing } = useStore()
  const isMobile = useIsMobile()
  const finalized = useMemo(() => invoice.single.is_finalised, [invoice.single.is_finalised])
  const isEditable = useMemo(() => invoice.single.is_editable, [invoice.single.is_editable])
  const showSidebar = useMemo(() => finalized, [finalized])
  const [edit, setEdit] = useState(false)
  const [paymentFormOpen, setPaymentFormOpen] = useState(false)
  const [emailFormOpen, setEmailFormOpen] = useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const _id = id && parseInt(id)

  if (!_id) return null

  const pageTitle = useMemo(() => {
    if (invoice.single.document_number) {
      return DYNAMIC_LABELS.INVOICE_NR(invoice.single.document_number)
    }
    if (invoice.single.is_editable && invoice.concept_number) {
      return DYNAMIC_LABELS.INVOICE_CONCEPT_NR(invoice.concept_number)
    }
    return LABELS.INVOICE
  }, [invoice.single.document_number, invoice.single.is_editable, invoice.concept_number])

  const getDefaultValues = async () => {
    return await invoice.getSingle(_id)
  }

  const form = useForm({
    resolver: yupResolver(documentValidationSchema),
    mode: 'all',
    defaultValues: getDefaultValues,
  })

  // Subscribe to form state to enable dirty tracking and error tracking
  // React-hook-form's formState is lazily evaluated - we need to subscribe to it
  React.useEffect(() => {
    const subscription = form.watch(() => {
      // Access formState to trigger tracking
      form.formState.isDirty
      form.formState.errors
      form.formState.isValid
    })
    return () => subscription.unsubscribe()
  }, [form])

  const handleBack = () => navigate('..')

  const handleSuccess = (newDocument: ISingleDocument) => {
    setEdit(false)
    form.reset(newDocument)
    ui.handleToaster(LABELS.CHANGES_SAVED, { type: 'success' })
  }

  const onSubmitCallback = async (values: FieldValues) => {
    try {
      const res = await invoice.update(values)
      handleSuccess(res)
    } catch (e: any) {
      handleErrors(e.errors, e.message)
    }
  }

  const handleErrors = (errors: Record<string, string[]>, message?: string) => {
    if (!errors) return

    form.clearErrors()
    const errorKeys = Object.keys(errors)

    errorKeys.forEach(singleKey => {
      // Handle nested errors like "lines.0.label" without type casting
      const errorMessages = errors[singleKey]
      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        form.setError(singleKey as any, {
          type: 'manual',
          message: errorMessages[0],
        })
      }
    })

    if (message) toast(message, { type: 'error' })
  }

  const handleFinalize = async () => {
    try {
      const res = await invoice.finalise()
      form.reset(res)
    } catch (e: any) {
      if (!e) return
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het definitief maken van de factuur', { type: 'error' })
    }
  }

  const handleSubmit = () => form.handleSubmit(onSubmitCallback)()

  const handleCancel = () => {
    form.reset()
    setEdit(false)
  }

  const handlePaymentSubmit = async (data: IPaymentFormData) => {
    try {
      await payment.addPayment(_id, data)
      await invoice.getSingle(_id)
      ui.handleToaster(PAYMENT_LABELS.SAVE_SUCCESS, { type: 'success' })
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan', { type: 'error' })
    }
  }

  const handlePaymentDelete = async (paymentId: number) => {
    try {
      await payment.deletePayment(_id, paymentId)
      await invoice.getSingle(_id)
      ui.handleToaster(PAYMENT_LABELS.DELETE_SUCCESS, { type: 'success' })
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan', { type: 'error' })
    }
  }

  const handleStatusChange = async (newStatus: PaymentStatus) => {
    try {
      await invoice.updatePaymentStatus(_id, newStatus)
      await invoice.getSingle(_id)
      await payment.getPayments(_id)
      ui.handleToaster('Status bijgewerkt', { type: 'success' })
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan', { type: 'error' })
    }
  }

  const handleCreateCreditNote = async () => {
    try {
      const creditNote = await invoice.createCreditNote(_id)
      ui.handleToaster('Creditnota aangemaakt', { type: 'success' })
      navigate(`/facturen/${creditNote.id}?edit=true`)
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het aanmaken van de creditnota', { type: 'error' })
    }
  }

  const handleDuplicate = async () => {
    try {
      const duplicatedInvoice = await invoice.duplicate(_id)
      ui.handleToaster('Factuur gedupliceerd', { type: 'success' })
      navigate(`/facturen/${duplicatedInvoice.id}?edit=true`)
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het dupliceren', { type: 'error' })
    }
  }

  const handlePreview = async () => {
    await ui.showModal({
      title: 'Voorvertoning',
      body: () => <DocumentPreviewModal onLoadPreview={() => invoice.previewPdf()} />,
      modalOptions: {
        maxWidth: 'xl',
        fullWidth: true,
      },
    }).catch(() => void 0)
  }

  const handleDownloadFormatSelect = async (format: DownloadFormat) => {
    setDownloadModalOpen(false)
    if (format === 'pdf') {
      await invoice.downloadPdf()
    } else if (format === 'pdf_ubl') {
      await invoice.downloadBulkZip([_id])
    }
  }

  const handleSendEmail = async () => {
    // Check if invoice has already been sent
    if (invoice.single.payment_status === 'sent') {
      try {
        await ui.confirm({
          title: 'E-mail al verstuurd',
          content:
            'Deze factuur is al eerder verstuurd. Bekijk de verzonden e-mail in je logboek onder ' +
            'Instellingen → E-mail. Wil je een nieuwe e-mail opstellen?',
          confirmLabel: 'Verder',
          cancelLabel: 'Annuleren',
          withCancel: true,
        })
        // User confirmed, open the email form
        setEmailFormOpen(true)
      } catch {
        // User cancelled, do nothing
      }
    } else {
      // Not sent yet, open the email form directly
      setEmailFormOpen(true)
    }
  }

  // Skeleton loading component for buttons
  const ButtonsSkeleton = () => (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      gap={1}
      width={isMobile ? '100%' : 'auto'}>
      {isMobile ? (
        <>
          <Skeleton
            variant="rounded"
            width="100%"
            height={36}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={36}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={36}
          />
        </>
      ) : (
        <>
          <Skeleton
            variant="rounded"
            width={120}
            height={36}
          />
          <Skeleton
            variant="rounded"
            width={120}
            height={36}
          />
          <Skeleton
            variant="rounded"
            width={120}
            height={36}
          />
        </>
      )}
    </Box>
  )

  const handleDelete = async () => {
    try {
      await invoice.delete(_id)
      ui.handleToaster('Factuur verwijderd', { type: 'success' })
      navigate('/facturen')
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het verwijderen van de factuur', { type: 'error' })
    }
  }

  // Check for edit parameter in URL and automatically enter edit mode
  useEffect(() => {
    const shouldEdit = searchParams.get('edit') === 'true'
    if (shouldEdit && isEditable && !edit) {
      setEdit(true)
      // Clean up the URL parameter after setting edit mode
      searchParams.delete('edit')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, isEditable, edit, setSearchParams])

  // Reload invoice data when ID changes
  useEffect(() => {
    const loadInvoice = async () => {
      const data = await invoice.getSingle(_id)
      form.reset(data)
      // Fetch concept number for draft invoices
      if (data && !data.document_number) {
        invoice.getConceptNumber(_id)
      }
    }
    loadInvoice()
  }, [_id])

  useEffect(() => {
    if (!shared.has_vat_codes) shared.getVatCodes()
    // Ensure company data is loaded for VAT code filtering
    if (!company.current_company) company.getMyCompany()
    if (edit) {
      if (!client.has_clients) client.getAll()
      if (!shared.has_countries) shared.getCountries()
    }
    if (showSidebar && _id) {
      payment.getPayments(_id)
    }
  }, [edit, shared.has_countries, shared.has_vat_codes, shared.has_clients, showSidebar, _id])

  return (
    <OvFormProvider
      loading={invoice.loading}
      form={form}
      readOnly={!edit}
      onSubmit={handleSubmit}
      onCancel={edit ? handleCancel : undefined}>
      <PageWrapper
        backAction={handleBack}
        pageTitle={
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}>
            {pageTitle}
            {!isEditable && invoice.single.payment_status && (
              <StatusBadge
                status={invoice.single.payment_status}
                type="payment"
              />
            )}
          </Box>
        }
        actions={
          invoice.loading ? (
            <ButtonsSkeleton />
          ) : (
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'stretch' : 'center'}
              justifyContent="flex-end"
              width={isMobile ? '100%' : 'auto'}
              gap={1}>
              {/* Edit Mode: Cancel + Save */}
              {edit && (
                <Box
                  display="flex"
                  gap={1}
                  width={isMobile ? '100%' : 'auto'}>
                  <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ flex: isMobile ? 1 : 'unset' }}>
                      {LABELS.CANCEL}
                    </Button>
                  </Tooltip>
                  <Tooltip title={TOOLTIPS.SAVE_SHORTCUT}>
                    <Button
                      size="small"
                      endIcon={<Save />}
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!form.formState.isValid}
                      sx={{ flex: isMobile ? 1 : 'unset' }}>
                      {LABELS.INVOICE_SAVE}
                    </Button>
                  </Tooltip>
                </Box>
              )}
              {/* Non-Edit Editable: Secondary actions + Primary action */}
              {!edit && isEditable && (
                <Box
                  display="flex"
                  gap={1}
                  width={isMobile ? '100%' : 'auto'}
                  flexWrap="wrap">
                  {!isMobile ? (
                    <>
                      <Tooltip title={TOOLTIPS.DELETE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          endIcon={<Delete />}
                          onClick={handleDelete}>
                          Verwijderen
                        </Button>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.DUPLICATE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ContentCopy />}
                          onClick={handleDuplicate}>
                          Dupliceren
                        </Button>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.EDIT_INVOICE}>
                        <Button
                          size="small"
                          endIcon={<Edit />}
                          variant="outlined"
                          onClick={() => setEdit(true)}>
                          {LABELS.INVOICE_EDIT}
                        </Button>
                      </Tooltip>
                      <Tooltip title="Bekijk een voorvertoning van de factuur">
                        <Button
                          size="small"
                          endIcon={<Visibility />}
                          variant="outlined"
                          onClick={handlePreview}>
                          Voorbeeld
                        </Button>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title={TOOLTIPS.DELETE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          endIcon={<Delete />}
                          onClick={handleDelete}
                          sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          Verwijderen
                        </Button>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.DUPLICATE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ContentCopy />}
                          onClick={handleDuplicate}
                          sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          Dupliceren
                        </Button>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.EDIT_INVOICE}>
                        <Button
                          size="small"
                          endIcon={<Edit />}
                          variant="outlined"
                          onClick={() => setEdit(true)}
                          sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          {LABELS.INVOICE_EDIT}
                        </Button>
                      </Tooltip>
                      <Tooltip title="Bekijk een voorvertoning van de factuur">
                        <Button
                          size="small"
                          endIcon={<Visibility />}
                          variant="outlined"
                          onClick={handlePreview}
                          sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          Voorbeeld
                        </Button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title={TOOLTIPS.FINALIZE_INVOICE}>
                    <Button
                      size="small"
                      variant="contained"
                      endIcon={<Check />}
                      onClick={handleFinalize}
                      sx={{ flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto', minWidth: isMobile ? 0 : 'auto' }}>
                      {LABELS.FINALISE}
                    </Button>
                  </Tooltip>
                </Box>
              )}
              {/* Finalized: Secondary actions + Primary action */}
              {!isEditable && (
                <Box
                  display="flex"
                  gap={1}
                  width={isMobile ? '100%' : 'auto'}
                  flexWrap="wrap">
                  {!isMobile ? (
                    <>
                      {invoice.single.payment_status && (
                        <StatusChangeMenu
                          currentStatus={invoice.single.payment_status}
                          onStatusChange={handleStatusChange}
                        />
                      )}
                      <Tooltip title={TOOLTIPS.CREATE_CREDIT_NOTE}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<SwapHoriz />}
                            onClick={handleCreateCreditNote}
                            disabled={invoice.single.payment_status === 'credited'}>
                            {LABELS.CREDIT}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.DUPLICATE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ContentCopy />}
                          onClick={handleDuplicate}>
                          Dupliceren
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={
                          !billing.isPro
                            ? 'Upgrade naar PRO om documenten via e-mail te versturen'
                            : !company.current_company?.can_send_emails
                            ? TOOLTIPS.CONFIGURE_SMTP_FIRST
                            : TOOLTIPS.SEND_INVOICE_EMAIL
                        }>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<Email />}
                            onClick={handleSendEmail}
                            disabled={!company.current_company?.can_send_emails || !billing.isPro}>
                            Versturen
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {invoice.single.payment_status && (
                        <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
                          <StatusChangeMenu
                            currentStatus={invoice.single.payment_status}
                            onStatusChange={handleStatusChange}
                          />
                        </Box>
                      )}
                      <Tooltip title={TOOLTIPS.CREATE_CREDIT_NOTE}>
                        <span style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<SwapHoriz />}
                            onClick={handleCreateCreditNote}
                            disabled={invoice.single.payment_status === 'credited'}
                            fullWidth>
                            Crediteer
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={TOOLTIPS.DUPLICATE_INVOICE}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ContentCopy />}
                          onClick={handleDuplicate}
                          sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          Dupliceren
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={
                          !billing.isPro
                            ? 'Upgrade naar PRO om documenten via e-mail te versturen'
                            : !company.current_company?.can_send_emails
                            ? TOOLTIPS.CONFIGURE_SMTP_FIRST
                            : TOOLTIPS.SEND_INVOICE_EMAIL
                        }>
                        <span style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<Email />}
                            onClick={handleSendEmail}
                            disabled={!company.current_company?.can_send_emails || !billing.isPro}
                            fullWidth>
                            Versturen
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip
                    title={
                      !company.current_company?.has_bank_account
                        ? TOOLTIPS.ADD_BANK_ACCOUNT_FIRST
                        : TOOLTIPS.DOWNLOAD_INVOICE_PDF
                    }>
                    <span
                      style={{
                        flex: isMobile ? '1 1 100%' : '0 0 auto',
                        minWidth: isMobile ? 0 : 'auto',
                      }}>
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<Download />}
                        onClick={() => setDownloadModalOpen(true)}
                        disabled={!company.current_company?.has_bank_account}
                        fullWidth={isMobile}>
                        {LABELS.DOWNLOAD}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )
        }
        fixedActions={edit && isEditable}>
        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={12}
            md={showSidebar ? 8 : 12}>
            {/* Display related documents */}
            <RelatedDocuments document={invoice.single} />
            <UpdateInvoiceForm documentType="invoice" />
          </Grid>
          {showSidebar && (
            <Grid
              item
              xs={12}
              md={4}>
              <Stack spacing={2}>
                <PaymentSummary document={invoice.single} />

                {invoice.single.payment_status === 'credited' &&
                  invoice.single.credit_notes &&
                  invoice.single.credit_notes.length > 0 && (
                    <OvCard>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}>
                        Creditnota&apos;s
                      </Typography>
                      <Stack spacing={1}>
                        {invoice.single.credit_notes.map(creditNote => (
                          <Button
                            key={creditNote.id}
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => navigate(`/invoice/${creditNote.id}`)}
                            sx={{ justifyContent: 'flex-start' }}>
                            {creditNote.document_number || `#${creditNote.number}`}
                          </Button>
                        ))}
                      </Stack>
                    </OvCard>
                  )}

                {invoice.single.is_credit_note && invoice.single.credited_invoice && (
                  <OvCard sx={{ bgcolor: 'warning.lighter' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}>
                      Creditnota voor
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => navigate(`/invoice/${invoice.single.credited_invoice!.id}`)}
                      sx={{ justifyContent: 'flex-start' }}>
                      {invoice.single.credited_invoice.document_number || `#${invoice.single.credited_invoice.number}`}
                    </Button>
                  </OvCard>
                )}

                <Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setPaymentFormOpen(true)}
                    disabled={invoice.single.payment_status === 'paid' || invoice.single.payment_status === 'credited'}>
                    {PAYMENT_LABELS.RECORD_PAYMENT}
                  </Button>
                </Box>
                <PaymentList
                  payments={payment.payments}
                  onDelete={handlePaymentDelete}
                  loading={payment.loading}
                />
              </Stack>
            </Grid>
          )}
        </Grid>
        <PaymentForm
          open={paymentFormOpen}
          onClose={() => setPaymentFormOpen(false)}
          onSubmit={handlePaymentSubmit}
          maxAmount={invoice.single.outstanding_amount}
          loading={payment.loading}
        />
        <SendEmailForm
          open={emailFormOpen}
          onClose={() => setEmailFormOpen(false)}
          documentId={_id}
          documentType="invoice"
          defaultValues={{
            recipient_email: invoice.single.client?.email || '',
            recipient_name: invoice.single.client?.name || '',
            attach_pdf: true,
          }}
          onSuccess={async () => {
            // Refresh invoice data to get updated status
            const updatedInvoice = await invoice.getSingle(_id)
            form.reset(updatedInvoice)
          }}
        />
        <DownloadFormatModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          onSelect={handleDownloadFormatSelect}
          loading={invoice.loading}
        />
      </PageWrapper>
    </OvFormProvider>
  )
})
